import { useAnalytics } from "@codat/analytics";
import {
  ArrowDownIcon,
  Button,
  CircleAlertIcon,
  Divider,
  Popover,
  SquareNewWindowIcon,
  Typography,
} from "@codat/orchard-ui";
import classNames from "classnames";
import { useSelect } from "downshift";
import { WhatsNewFeedItem } from "hooks/useParseWhatsNewFeed";
import React, { FC, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import ChangelogItem from "components/ChangelogItem";
import NavButton from "components/NavButton";

import styles from "./NavDropdown.module.scss";

export interface NavDropdownItem {
  buttonText: string;
  path: string | undefined;
  parentPath?: string;
  permission?: boolean;
  onClick?: () => void;
  href?: string;
  feed?: Omit<WhatsNewFeedItem, "link">;
}

export interface NavDropdownProps {
  trigger?: React.ReactNode;
  header?: {
    icon: React.ReactNode;
    title: string;
    cta?: {
      text: string;
      href: string;
    };
  };
  eventProperties: Record<string, string>;
  "data-testid": string;
  menuItems: NavDropdownItem[];
  label: string;
  errorMessage?: string;
  isLoading?: boolean;
  activeButton?: string;
  pauseFocusTrap?: boolean; // set to true when you layer focus traps (e.g. clicking on a nav item opens a modal)
  className?: string;
}

export const NavDropdown: FC<NavDropdownProps> = ({
  trigger,
  header,
  eventProperties,
  activeButton,
  menuItems,
  isLoading,
  label,
  "data-testid": testId,
  className,
  errorMessage = "",
}) => {
  const { location } = useHistory();
  const { sendEvent } = useAnalytics();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<NavDropdownItem>(
    menuItems.find((i) => i.path === location.pathname) || menuItems[0]
  );

  const handleSelectedItemChange = (navMenuItem: NavDropdownItem) => {
    navMenuItem.onClick && navMenuItem.onClick();
    const properties = navMenuItem.href
      ? { URL: navMenuItem.href }
      : { page: navMenuItem.buttonText };
    sendEvent(`${label} Dropdown - Item - Click`, properties);
    closeMenu();
    if (!navMenuItem.path) return;
    setSelectedItem(navMenuItem);
  };

  const {
    isOpen,
    closeMenu,
    getToggleButtonProps,
    highlightedIndex,
    getMenuProps,
    getItemProps,
  } = useSelect<NavDropdownItem>({
    items: menuItems,
    selectedItem,
    onSelectedItemChange: ({ selectedItem: selected }) =>
      selected && handleSelectedItemChange(selected),
  });

  return (
    <div>
      <Popover
        data-testid={`${label.toLowerCase()}-popover`}
        popoverClassName={styles.navBarPopover}
        className={className}
        isOpen={isOpen}
        onClose={closeMenu}
        placement={header ? "bottom-end" : "bottom-start"}
        removePadding
        alwaysInDom
        content={(isOpenAndAnimating) => (
          <ul
            className={classNames(styles.navDropdownList, {
              [styles.customDropdown]: header,
              [styles.supportDropdown]: header?.title === "Support",
              [styles.whatsNewDropdown]: header?.title === "What's new",
            })}
            {...getMenuProps({}, { suppressRefError: true })}
          >
            {header && (
              <>
                <div className={styles.header}>
                  {header.icon}
                  <Typography as="h3" variant="heading_lg">
                    {header.title}
                  </Typography>
                  {header.cta && (
                    <Button
                      variant="secondary"
                      size="small"
                      color="interface"
                      data-testid={`${header.cta.text}-button`}
                      href={header.cta.href}
                      target="_blank"
                      className={styles.headerCta}
                      onClick={() =>
                        sendEvent(`${header?.cta?.text} Button - Click`)
                      }
                    >
                      <span>{header.cta.text}</span>
                      <SquareNewWindowIcon />
                    </Button>
                  )}
                </div>
                <Divider />
              </>
            )}
            {isOpenAndAnimating && errorMessage && (
              <div className={styles.error}>
                <CircleAlertIcon />
                <Typography as="p" variant="heading_md">
                  {errorMessage}
                </Typography>
              </div>
            )}
            {isOpenAndAnimating &&
              !errorMessage &&
              menuItems.map(
                (item, index) =>
                  item.permission && (
                    <React.Fragment key={index}>
                      <li
                        className={classNames(styles.item, {
                          [styles.highlight]: highlightedIndex === index,
                          [styles.whatsNewNoHighlight]: item.feed && !item.href,
                        })}
                        key={`${testId}-item-${index}`}
                        {...getItemProps({ item, index })}
                      >
                        <Button
                          to={item.path}
                          href={item.href}
                          variant="tertiary"
                          color="interface"
                          data-testid={`${testId}-item-${index}`}
                          {...(item.path === location.pathname ||
                          (item.parentPath &&
                            location.pathname.startsWith(item.parentPath))
                            ? { "aria-current": "page" }
                            : {})}
                          className={styles.navItem}
                          target={item.href ? "_blank" : undefined}
                          disabled={!item.href && !item.path && !item.onClick}
                        >
                          {item.feed ? (
                            <ChangelogItem
                              key={`${item.feed.date}-${index}`}
                              description={item.feed.description}
                              date={item.feed.date}
                              tags={item.feed.tags}
                            />
                          ) : (
                            <>
                              <span
                                className={classNames(
                                  styles.dropdownButtonLabel,
                                  {
                                    [styles.isSelected]:
                                      (item.path &&
                                        item.path === activeButton) ||
                                      (item.parentPath &&
                                        activeButton?.startsWith(
                                          item.parentPath
                                        )),
                                    [styles.customDropdownLabel]: header,
                                  }
                                )}
                              >
                                {item.buttonText}
                              </span>
                              {item.href && (
                                <SquareNewWindowIcon
                                  className={styles.newWindowIcon}
                                />
                              )}
                            </>
                          )}
                        </Button>
                      </li>
                      {item.feed && index !== 2 && (
                        <Divider className={styles.changelogItemDivider} />
                      )}
                    </React.Fragment>
                  )
              )}
          </ul>
        )}
      >
        {trigger ? (
          <div
            ref={buttonRef}
            {...getToggleButtonProps({}, { suppressRefError: true })}
            data-testid={testId}
            className={classNames(styles.item, {
              [styles.isActive]: isOpen,
            })}
          >
            {trigger}
          </div>
        ) : (
          <NavButton
            eventProperties={eventProperties}
            isActive={location.pathname.startsWith("/" + label.toLowerCase())}
            ref={buttonRef}
            {...getToggleButtonProps(
              { ref: buttonRef },
              { suppressRefError: true }
            )}
            isSkeleton={isLoading}
            isDropdownButton
            data-testid={testId}
          >
            <span>{label}</span>
            <ArrowDownIcon className={styles.navDropdownIcon} />
          </NavButton>
        )}
      </Popover>
    </div>
  );
};
